import { Box, Table } from '@approvalmax/ui/src/components';
import { ColumnDefinition, SortOrder } from '@approvalmax/ui/src/components/Table/Table.types';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { memo, useCallback, useMemo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { changeReportSorting, reorderReportColumns } from '../../actions';
import { getLineItemColumnDefinitionByKind } from '../../config/lineItem/columnLineItemDefinitions';
import LineItemColumnKind from '../../config/lineItem/lineItemColumnKind';
import { getColumnDefinitionByKind } from '../../config/standard/columnDefinitions';
import ColumnKind from '../../config/standard/columnKind';
import { ReportRowData } from '../../types/ReportRowData';
import { EmptyCell } from './components';
import { mapSortOrdersFromLegacy, mapSortOrdersToLegacy } from './ReportTable.helpers';
import { ReportTableProps } from './ReportTable.types';

export const ReportTable = memo<ReportTableProps>((props) => {
    const { reportConfig, columns, data, loading } = props;

    const dispatch = useDispatch();
    const company = useCompanyBasedOnUrl();

    const handleColumnSort = useCallback(
        (columnId: string, sortOrder: SortOrder) => {
            const column = columns.find((col) => col.id === columnId);

            if (!column || !column.sortable) {
                return;
            }

            dispatch(
                changeReportSorting({
                    column,
                    sorting: mapSortOrdersToLegacy(sortOrder),
                })
            );
        },
        [columns, dispatch]
    );

    /**
     * This callback is needed to combine new table reordering approach
     * with redux reports logic
     *
     * Table can only reorder columns between the columns passed in, but report reordering occurs between all existing columns,
     * so we need to synchronize the indexes using passed index to column definitions
     */
    const handleReorderColumns = useCallback(
        (
            _columnsNext: ColumnDefinition<ReportRowData>[],
            indexPrev: number,
            indexNext: number,
            columnsPrev: ColumnDefinition<ReportRowData>[]
        ) => {
            const columnsAllIndexPrev = columnsPrev[indexPrev].index;
            const columnsAllIndexNext = columnsPrev[indexNext].index;

            if (
                columnsAllIndexPrev !== undefined &&
                columnsAllIndexNext !== undefined &&
                columnsAllIndexPrev !== columnsAllIndexNext
            ) {
                dispatch(
                    reorderReportColumns({
                        columns,
                        oldIndex: columnsAllIndexPrev,
                        newIndex: columnsAllIndexNext,
                    })
                );
            }
        },
        [columns, dispatch]
    );

    const columnsDefinition = useMemo<ColumnDefinition<ReportRowData>[]>(() => {
        const columnsList =
            columns.map((column, index) => ({ column, index })).filter((item) => item.column.visible) || [];

        return columnsList.map((item, columnIndex) => {
            const { column, index } = item;

            const { cellComponent: CellComponent } =
                reportConfig.reportType === domain.ReportType.LineItem && reportConfig.reportCode
                    ? getLineItemColumnDefinitionByKind(column.kind as LineItemColumnKind, reportConfig.reportCode)
                    : getColumnDefinitionByKind(column.kind as ColumnKind, company);

            return {
                index,
                id: column.id,
                name: column.name,
                sortable: column.sortable,
                sortOrder: mapSortOrdersFromLegacy(column.sorting),
                width: column.width,
                minWidth: column.minWidth,
                value: (row) => {
                    if (loading) return null;

                    const value = row.cells[columnIndex];

                    if (
                        value === '' ||
                        value === null ||
                        (Array.isArray(value) && value.length === 0) ||
                        (typeof value === 'object' && 'text' in value && value.text === '')
                    ) {
                        return null;
                    }

                    return Array.isArray(value) ? value : [value];
                },
                cellComponent: ({ item, value, 'data-column-id': dataColumnId }) => (
                    <Box as='td' spacing='12 8' data-column-id={dataColumnId}>
                        {value === null && <EmptyCell />}

                        {Array.isArray(value) &&
                            value.map((val, key) =>
                                value ? <CellComponent key={key} value={val} rowId={item.id} /> : null
                            )}
                    </Box>
                ),
            };
        });
    }, [columns, company, loading, reportConfig.reportCode, reportConfig.reportType]);

    return (
        <Table
            qa='reports'
            headerColor='white100'
            columns={[...columnsDefinition]}
            data={loading ? [] : [...data]}
            getRowId={(item) => item.id}
            onSort={handleColumnSort}
            progress={loading}
            reorderable
            onReorderColumns={handleReorderColumns}
        />
    );
});

ReportTable.displayName = 'ReportTable';
